import { useEffect, useState } from 'react';

import { HeroEmailCapture } from '../components/marketing/HeroEmailCapture';
import { MarketingSiteLayout } from '../components/marketing/MarketingSiteLayout';
import { ShareBand } from '../components/marketing/ShareBand';
import {
  beaconForDate,
  buildDateIso,
  formatPromptDate,
  isoDate,
  voicePromptForDate,
} from '../prompts/dailyPrompt';
import './promptPages.css';

/**
 * /prompt — one beacon a day, and a voice prompt to go with it.
 *
 * There is no server at runtime: the shell is prerendered at deploy with the build date's
 * prompt, and the bundle's first render uses the same date so hydration keeps the markup.
 * After mount the page moves to the reader's own day. A crawler sees the deploy day's prompt,
 * which is fine — any prompt is the point; the archive at /prompts carries all of them.
 */
export function DailyPromptPage() {
  const [date, setDate] = useState(buildDateIso);
  useEffect(() => {
    setDate(isoDate(new Date()));
  }, []);

  const beacon = beaconForDate(date);
  const voice = voicePromptForDate(date);

  return (
    <MarketingSiteLayout>
      <article className="prompt-page">
        <header className="prompt-hero">
          <p className="prompt-kicker">Today’s prompt · {formatPromptDate(date)}</p>
          <h1 className="prompt-h1">{beacon.question}</h1>
          <p className="prompt-hint">{beacon.hint}</p>
          <p className="prompt-lead">
            One question a day from the beacons: the questions Kynward members answer on their
            profile so a stranger has somewhere to start. Ask it at dinner tonight, or answer it in
            the app and let a Circle ask you the next one.
          </p>
          <div className="prompt-cta">
            <HeroEmailCapture source="prompt-page" inputId="prompt-waitlist-email" />
            <a className="prompt-link" href="/prompts">
              Every prompt, in one place →
            </a>
          </div>
        </header>

        <section className="prompt-section" aria-labelledby="prompt-voice">
          <h2 id="prompt-voice" className="prompt-h2">
            Say it out loud
          </h2>
          <p className="prompt-voice-q">{voice.question}</p>
          <p className="prompt-p">
            A voice prompt: eighteen seconds, one take, no script. Members answer one of these on
            their profile, because the way someone says a thing carries more than the thing itself.
          </p>
        </section>

        <section className="prompt-section" aria-labelledby="prompt-why">
          <h2 id="prompt-why" className="prompt-h2">
            Why a question, not a bio
          </h2>
          <p className="prompt-p">
            A profile that lists facts gives a stranger nothing to say. A question with a real
            answer behind it hands them the first line. Every Kynward profile carries six beacons,
            and a Circle of four to six people opens on one prompt everyone answers.
          </p>
          <p className="prompt-p">
            <a className="prompt-link" href="/">
              How Kynward works →
            </a>
          </p>
        </section>

        <ShareBand />
      </article>
    </MarketingSiteLayout>
  );
}
